import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Report } from '@app/core/models/analytics.model';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import {
  AnalyticsActions,
  AnalyticsSelectors,
  AuthSelectors,
  GlobalSelectors,
  SiteActions,
  UserDataSelectors,
} from '@app/store';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { SSRS_REPORT_BASE_URL } from '@app/shared/app.constants';
import { User } from '@app/core/models';
import { BookADemoDialogComponent } from '@app/shared/components/book-ademo-dialog/book-ademo-dialog.component';
import { SupportTicketSubmitModalComponent } from '@app/shared/components/support-ticket-submit-modal/support-ticket-submit-modal.component';
import { ActivatedRoute } from '@angular/router';
import { ReportViewerComponent } from '../../components/report-viewer/report-viewer.component';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent implements OnInit, OnDestroy {
  user$: Observable<User> = this.store.select(AuthSelectors.getUser);
  reportsList: Report[] = [];
  originalReportsList: Report[] = [];
  subscriptions: Subscription = new Subscription();
  userInfo$ = this.store.select(UserDataSelectors.getUserInfo);
  reportURL: string | null = null;
  reportViwerDialogRef: MatDialogRef<ReportViewerComponent, any> = null;
  bookADemoDialogRef: MatDialogRef<BookADemoDialogComponent, any> = null;
  demoRequestSuccessDialogRef: MatDialogRef<
    SupportTicketSubmitModalComponent,
    any
  > = null;
  userName: string | null = null;
  email: string;
  reportCategory: string;

  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute
  ) {}

  private subscribeToUserChanges(): void {
    this.subscriptions.add(
      this.user$.subscribe(user => {
        if (user && user.name.length) {
          this.userName = user.name;
        }
      })
    );
  }

  private openReportViewer(): void {
    this.reportViwerDialogRef = this.dialog.open(ReportViewerComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: '85vw',
      height: '90vh',
      panelClass: 'report-dialog-container',
      data: {
        reportURL: this.reportURL,
      },
    });

    this.reportViwerDialogRef.afterClosed().subscribe(() => {
      this.reportURL = null;
      this.store.dispatch(AnalyticsActions.resetReportToken());
    });
  }

  private subscribeToReportToken(): void {
    this.subscriptions.add(
      this.store
        .select(AnalyticsSelectors.getReportToken)
        .pipe(filter(token => !!token))
        .subscribe(token => {
          this.reportURL = `${this.reportURL}&report_execution_token=${token}`;
          this.openReportViewer();
        })
    );
  }

  private subscribeToGlobalFilters(): void {
    this.subscriptions.add(
      this.store
        .select(GlobalSelectors.getSelectedGlobalFiltersWithDetails)
        .pipe(
          filter(selectedBillingAcc => selectedBillingAcc.length > 0),
          distinctUntilChanged((prev, curr) => {
            return JSON.stringify(prev) === JSON.stringify(curr);
          })
        )
        .subscribe(selectedBillingAcc => {
          this.reportsList = this.transformReportList(
            this.originalReportsList,
            selectedBillingAcc
          );
        })
    );
  }

  showReportsListLoading$: Observable<boolean> = this.store.select(
    AnalyticsSelectors.showReportsListLoading
  );

  isDemoRequestsuccess$: Observable<boolean> = this.store.select(
    AnalyticsSelectors.isDemoRequestsuccess
  );

  ngOnInit(): void {
    this.activatedRoute.url.subscribe(segments => {
      if (segments.length > 0) {
        const lastSegment = segments[segments.length - 1].path;

        this.reportCategory = lastSegment;
      }
    });
    this.subscribeToUserChanges();
    this.subscribeToReportList();
    this.fetchReportsList();
    this.subscribeToReportToken();

    this.store.dispatch(SiteActions.getActiveSites({ billingAccountIds: [] }));

    this.subscriptions.add(
      this.isDemoRequestsuccess$.subscribe(success => {
        if (success !== null) {
          this.demoRequestSuccessDialogRef = this.dialog.open(
            SupportTicketSubmitModalComponent,
            {
              disableClose: true,
              data: {
                demoRequest: success,
              },
            }
          );

          this.email = null;
          this.store.dispatch(AnalyticsActions.resetAnalyticDemoRequest());
        }
      })
    );
  }

  public openBookADemoDialog(): void {
    this.bookADemoDialogRef = this.dialog.open(BookADemoDialogComponent, {
      maxWidth: '90%',
      data: { email: this.email },
    });

    this.bookADemoDialogRef.afterClosed().subscribe(emailId => {
      this.email = emailId;
    });
  }

  private fetchReportsList() {
    this.store.dispatch(AnalyticsActions.showReportsListLoading());
    this.store.dispatch(AnalyticsActions.getReportList());
  }

  private transformReportList(
    reportsList: Report[],
    selectedBillingAcc: any[]
  ): Report[] {
    return reportsList.map(report => {
      const reportFilters = report.reportFilters.map(filter => {
        switch (filter.name) {
          case 'customer_account': {
            const customerAccountOptions = selectedBillingAcc
              .filter(
                account =>
                  account.accountNumber !== null &&
                  account.accountNumber !== undefined
              )
              .map(({ id, accountNumber, name, sites }) => {
                return {
                  id,
                  value: accountNumber,
                  label: name,
                  sites,
                };
              });

            return {
              ...filter,
              options: customerAccountOptions,
            };
          }

          case 'region_id': {
            return {
              ...filter,
              options: filter.options,
            };
          }

          case 'region_site': {
            return {
              ...filter,
              options: filter.options,
            };
          }

          case 'location_account': {
            return {
              ...filter,
              options: filter.options,
            };
          }

          default: {
            return filter;
          }
        }
      });

      return { ...report, reportFilters: reportFilters };
    });
  }

  private subscribeToReportList(): void {
    this.subscriptions.add(
      this.store
        .select(AnalyticsSelectors.getReportList)
        .pipe(filter(reportsList => reportsList.length > 0))
        .subscribe(reportsList => {
          const reportsListByCategory = reportsList.filter(
            n =>
              n.reportCategory.toLowerCase() ===
              this.reportCategory.toLowerCase()
          );

          this.originalReportsList = reportsListByCategory; // reportsList;
          this.reportsList = this.transformReportList(
            reportsListByCategory,
            []
          );
          this.subscribeToGlobalFilters();
        })
    );
  }

  private getReportParameters(jsonData: any): any[] {
    const params: any[] = [];

    Object.keys(jsonData).forEach(key => {
      if (key === 'begin_end_date' && jsonData[key] !== null) {
        const subParams = this.getReportParameters(jsonData[key]);

        params.push(...subParams);
      } else if (key === 'region_site') {
        params.push({
          ParamName: 'location_account',
          ParamValue: jsonData[key],
        });
      } else {
        params.push({ ParamName: key, ParamValue: jsonData[key] });
      }
    });

    return params;
  }

  onReportSelect(selectedReport: Report) {
    const selectedReports = this.reportsList.filter(r => r.selected);

    if (selectedReports.length) {
      selectedReports.map(report => {
        report.selected = false;
        return report;
      });
    }

    const foundReport = this.reportsList.find(
      report => report.id === selectedReport.id
    );

    if (foundReport) {
      foundReport.selected = true;
    }
  }

  onFormSubmit({ form, report }: { form: FormGroup<any>; report: Report }) {
    // TODO: show loading screen
    if (!form.invalid && report.reportNameSSRS && this.userName) {
      const formValues = form.value;

      report.loading = true;
      // Report URL
      this.reportURL = `${SSRS_REPORT_BASE_URL}${report.reportNameSSRS
        .split(' ')
        .join('+')}&rs:Command=Render&rs:embed=true`;

      report.reportFilters.map(reportFilter => {
        if (reportFilter?.visible === false) {
          delete formValues[reportFilter.name];
        }
      });

      const data = {
        reportUrl: this.reportURL,
        createdUser: this.userName,
        reportParameters: this.getReportParameters(formValues),
      };

      this.store.dispatch(
        AnalyticsActions.getReportToken({ requestBody: data })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
